/**
 * AcceptInvitePage - Page for accepting collaboration space invites
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { collaborationApi } from '../services/api';
import { cn } from '../lib/utils';

const AcceptInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const { isAuthenticated, user } = useAuth();
  const colors = getThemeColors();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [spaceId, setSpaceId] = useState(null);

  // Load invite information
  useEffect(() => {
    const loadInvite = async () => {
      try {
        setLoading(true);
        const response = await collaborationApi.getInviteInfo(token);
        setInviteInfo(response.data);
      } catch (err) {
        console.error('Failed to load invite:', err);
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadInvite();
    }
  }, [token]);

  // Accept the invitation
  const handleAccept = async () => {
    setAccepting(true);
    try {
      const response = await collaborationApi.acceptInvite(token);
      setAccepted(true);
      setSpaceId(response.data.space_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center", colors.bgPrimary)}>
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
        <Card className={cn("p-8 max-w-md text-center", colors.bgCard, colors.borderPrimary)}>
          <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>
            Invitation Error
          </h1>
          <p className={cn("mb-6", colors.textSecondary)}>
            {error}
          </p>
          <Button onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
        <Card className={cn("p-8 max-w-md text-center", colors.bgCard, colors.borderPrimary)}>
          <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
          <h1 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>
            Invitation Accepted!
          </h1>
          <p className={cn("mb-6", colors.textSecondary)}>
            You now have access to &ldquo;{inviteInfo?.space_name}&rdquo;.
          </p>
          <Button 
            className={colors.accentGradient}
            onClick={() => navigate(`/collaboration/${spaceId}`)}
          >
            Open Collaboration Space
          </Button>
        </Card>
      </div>
    );
  }

  // If not authenticated, prompt to login
  if (!isAuthenticated) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
        <Card className={cn("p-8 max-w-md text-center", colors.bgCard, colors.borderPrimary)}>
          <div className={cn("w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600")}>
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>
            You&apos;ve Been Invited!
          </h1>
          <p className={cn("mb-2", colors.textSecondary)}>
            <strong>{inviteInfo?.inviter_name}</strong> invited you to collaborate on
          </p>
          <p className={cn("text-lg font-semibold mb-6", colors.textPrimary)}>
            &ldquo;{inviteInfo?.space_name}&rdquo;
          </p>
          <p className={cn("text-sm mb-6", colors.textMuted)}>
            Please log in or create an account to accept this invitation.
          </p>
          <Button 
            className={cn("w-full", colors.accentGradient)}
            onClick={() => navigate(`/login?redirect=/collaboration/invite/${token}`)}
          >
            Log In to Accept
          </Button>
        </Card>
      </div>
    );
  }

  // Show accept button for authenticated users
  return (
    <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
      <Card className={cn("p-8 max-w-md text-center", colors.bgCard, colors.borderPrimary)}>
        <div className={cn("w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600")}>
          <Users className="w-8 h-8 text-white" />
        </div>
        <h1 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>
          Join Collaboration Space
        </h1>
        <p className={cn("mb-2", colors.textSecondary)}>
          <strong>{inviteInfo?.inviter_name}</strong> invited you to collaborate on
        </p>
        <p className={cn("text-lg font-semibold mb-6", colors.textPrimary)}>
          &ldquo;{inviteInfo?.space_name}&rdquo;
        </p>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            className="flex-1"
            onClick={() => navigate('/products')}
          >
            Decline
          </Button>
          <Button 
            className={cn("flex-1", colors.accentGradient)}
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Accept Invite
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AcceptInvitePage;
